import React from 'react';
import { CheckCircle, Package, MapPin, Clock, CreditCard, Home, ChevronRight, Wallet } from 'lucide-react';
import { getOrderItemImage } from '../../utils/imageHelpers';

const formatOrderDate = () => {
  const d = new Date();
  const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
  const day = d.getDate();
  const month = d.toLocaleDateString('en-IN', { month: 'long' });
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = (hours % 12 || 12).toString().padStart(2, '0');
  return `${weekday}, ${day} ${month} ${year} at ${hour12}:${minutes}${ampm}`;
};

const DEFAULT_SLOTS = [
  { id: 'morning',   label: 'Morning',   time: '6:00 AM – 8:00 AM', icon: '🌅', cutoffHour: 4  },
  { id: 'afternoon', label: 'Afternoon', time: '2:00 PM – 4:00 PM', icon: '☀️', cutoffHour: 12 },
  { id: 'evening',   label: 'Evening',   time: '6:00 PM – 8:00 PM', icon: '🌆', cutoffHour: 16 },
];

const isSlotEnabled = (s) =>
  s.enabled !== false && s.enabled !== 0 && s.enabled !== '0' && s.enabled !== 'false';

const buildSlotMap = (slots) =>
  (slots && slots.length ? slots : DEFAULT_SLOTS)
    .filter(isSlotEnabled)
    .reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

// Returns the next available calendar date for a given slot.
// Orders placed before the slot's cutoffHour are eligible for same-day delivery;
// otherwise the next day is returned.
const getNextSlotDate = (slotId, slotMap) => {
  const map = slotMap || buildSlotMap(null);
  const slot = map[slotId] ?? map.morning ?? DEFAULT_SLOTS[0];
  const now = new Date();
  const date = new Date(now);
  if (now.getHours() >= (slot.cutoffHour ?? 0)) {
    date.setDate(date.getDate() + 1);
  }
  return date;
};

const formatDeliveryDate = (date) => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dayLabel =
    date.toDateString() === now.toDateString()     ? 'Today' :
    date.toDateString() === tomorrow.toDateString() ? 'Tomorrow' :
    date.toLocaleDateString('en-IN', { weekday: 'long' });

  const dateStr = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${dayLabel}, ${dateStr}`;
};

const paymentMethodLabels = {
  cod:      { label: 'Cash on Delivery',           icon: '💵' },
  upi:      { label: 'UPI Payment',                icon: '📱' },
  card:     { label: 'Card Payment',               icon: '💳' },
  wallet:   { label: 'Wallet',                     icon: '👛' },
  upi_cod:  { label: 'Cash on Delivery (UPI)',     icon: '📱' },
  razorpay: { label: 'Razorpay (Online)',         icon: '⚡' },
  payu:     { label: 'PayU (Online)',             icon: '🔒' },
  netbanking:{ label: 'Net Banking',               icon: '🏦' },
  online:   { label: 'Online Payment',            icon: '🔒' },
};

const OrderConfirmationModal = ({
  isOpen,
  orderDetails,
  onViewOrders,
  onContinueShopping,
  onContactSupport,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
  deliverySlots,
}) => {
  if (!isOpen) return null;

  const {
    orderId,
    orderNumber,
    items,
    totalAmount,
    originalAmount,
    couponCode,
    couponDiscount,
    shippingCharge,
    taxAmount,
    handlingCharge,
    walletAmountUsed,
    deliverySlot,
    paymentMethod,
    deliveryAddress,
  } = orderDetails || {};

  const slotMap = buildSlotMap(deliverySlots);
  const selectedSlot = slotMap[deliverySlot] || slotMap.morning || DEFAULT_SLOTS[0];
  const slotDeliveryDate = getNextSlotDate(deliverySlot, slotMap);
  const formattedDeliveryDate = formatDeliveryDate(slotDeliveryDate);

  // Normalize a variety of backend/frontend payment identifiers into our label keys
  const normalizePaymentKey = (raw) => {
    if (!raw) return null;
    const r = String(raw).toLowerCase().trim();
    if (r === 'cod') return 'cod';
    if (r === 'upi_cod' || (r.includes('upi') && r.includes('cod'))) return 'upi_cod';
    if (r.includes('razor')) return 'razorpay';
    if (r.includes('payu')) return 'payu';
    if (r.includes('wallet')) return 'wallet';
    if (r.includes('card')) return 'card';
    if (r.includes('netbank')) return 'netbanking';
    if (r.includes('upi')) return 'upi';
    if (r.includes('online')) return 'online';
    return r;
  };

  const rawPayment = orderDetails?.paymentMethod || orderDetails?.payment_method || orderDetails?.payment_gateway || paymentMethod;
  const normalizedKey = normalizePaymentKey(rawPayment) || (walletAmountUsed > 0 ? 'wallet' : 'cod');
  const selectedPayment = paymentMethodLabels[normalizedKey] || paymentMethodLabels.cod;

  // Derive a friendly gateway/provider label to show alongside the payment method
  const gatewayMap = {
    razorpay: 'Razorpay',
    payu: 'PayU',
    stripe: 'Stripe',
    paypal: 'PayPal',
  };

  const rawGateway = orderDetails?.payment_gateway || orderDetails?.gateway || orderDetails?.paymentMethodGateway || null;
  const gatewayKeyCandidate = rawGateway ? normalizePaymentKey(rawGateway) : (['razorpay', 'payu', 'stripe', 'paypal'].includes(normalizedKey) ? normalizedKey : null);
  const gatewayLabel = gatewayKeyCandidate ? (gatewayMap[gatewayKeyCandidate] || gatewayKeyCandidate.charAt(0).toUpperCase() + gatewayKeyCandidate.slice(1)) : null;

  const card = (children) => (
    <div className="bg-white p-4" style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
      {children}
    </div>
  );

  const summaryRow = (label, value, { color, bold } = {}) => (
    <div className="flex items-center justify-between py-1.5">
      <span style={{ fontSize: 14, color: bold ? 'var(--fx-ink)' : 'var(--fx-ink-2)', fontWeight: bold ? 700 : 400 }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: bold ? 700 : 400, color: color || 'var(--fx-ink)' }}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="fashion-scope min-h-full bg-[#fafafa] pb-6">

      {/* ── Confirmation banner ── */}
      <div className="bg-white px-6 py-10 text-center" style={{ borderBottom: '1px solid var(--fx-line)' }}>
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: '#e8f7f3' }}
        >
          <CheckCircle size={34} style={{ color: 'var(--fx-green)' }} />
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--fx-ink)' }}>
          Order Confirmed
        </h1>
        <p className="mt-1.5" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
          Thank you! We'll send updates on WhatsApp and email.
        </p>

        <div className="inline-block mt-5 px-4 py-2" style={{ background: '#f5f5f6', borderRadius: 4 }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--fx-ink-3)' }}>
            ORDER ID
          </p>
          <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--fx-ink)' }}>
            #{orderNumber || orderId}
          </p>
        </div>
        <p className="mt-2" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>{formatOrderDate()}</p>
      </div>

      {/* ── Body ── */}
      <div className="px-3 py-4 space-y-3 max-w-[720px] mx-auto">

        {/* Estimated delivery — the thing shoppers look for first */}
        {card(
          <div className="flex items-start gap-3">
            <Clock size={18} style={{ color: 'var(--fx-ink-2)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="fx-label">Estimated Delivery</p>
              <p className="mt-1" style={{ fontSize: 15, fontWeight: 700, color: 'var(--fx-green)' }}>
                {formattedDeliveryDate}
              </p>
              <p style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
                {selectedSlot.label} · {selectedSlot.time}
              </p>
            </div>
          </div>
        )}

        {/* Items */}
        {card(
          <>
            <h3 className="fx-label mb-3">
              <Package size={15} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
              Items ({items?.length || 0})
            </h3>
            <div className="space-y-3">
              {items?.map((item, index) => {
                const unitPrice = item.price || 0;
                const lineTotal = item.subtotal || (unitPrice * item.quantity).toFixed(2);
                const hasDiscount = item.discount;

                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-shrink-0">{getOrderItemImage(item, 'small')}</div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{item.name}</p>
                      <p style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                        {[item.variant_name, [item.weight, item.unit].filter(Boolean).join(' ')]
                          .filter(Boolean).join(' · ')}
                        {[item.variant_name, item.weight, item.unit].some(Boolean) ? ' · ' : ''}
                        Qty {item.quantity}
                      </p>
                      {hasDiscount && (
                        <p style={{ fontSize: 12 }}>
                          <span style={{ fontWeight: 700 }}>₹{item.product?.sale_price}</span>
                          <span style={{ color: 'var(--fx-ink-3)', textDecoration: 'line-through', marginLeft: 6 }}>
                            ₹{item.product?.price}
                          </span>
                        </p>
                      )}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, flexShrink: 0 }}>₹{lineTotal}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Delivery address */}
        {deliveryAddress && card(
          <div className="flex items-start gap-3">
            <MapPin size={18} style={{ color: 'var(--fx-ink-2)', flexShrink: 0, marginTop: 2 }} />
            <div className="min-w-0">
              <p className="fx-label">Delivery Address</p>
              <p className="mt-1" style={{ fontSize: 14, color: 'var(--fx-ink-2)', lineHeight: 1.5 }}>
                {deliveryAddress.address || deliveryAddress.street || ''}
              </p>
              <p style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
                {[deliveryAddress.city, deliveryAddress.state].filter(Boolean).join(', ')}
                {deliveryAddress.pincode ? ` - ` : ''}
                {deliveryAddress.pincode && (
                  <span style={{ fontWeight: 700, color: 'var(--fx-ink)' }}>{deliveryAddress.pincode}</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Payment */}
        {card(
          <div className="flex items-start gap-3">
            <CreditCard size={18} style={{ color: 'var(--fx-ink-2)', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <p className="fx-label">Payment Method</p>
              <p className="mt-1" style={{ fontSize: 14, fontWeight: 700 }}>
                {selectedPayment.label}
                {gatewayLabel && (
                  <span style={{ fontWeight: 400, color: 'var(--fx-ink-2)' }}> via {gatewayLabel}</span>
                )}
              </p>
              {walletAmountUsed > 0 && (
                <p className="mt-1 flex items-center gap-1" style={{ fontSize: 13, color: 'var(--fx-green)' }}>
                  <Wallet size={13} />
                  ₹{walletAmountUsed} deducted from wallet
                </p>
              )}

              {paymentMethod === 'upi_cod' && (
                <div className="mt-4 flex flex-col items-center">
                  <img
                    src={upiQrCode}
                    alt="UPI QR Code"
                    style={{ width: 180, height: 180, display: 'block', borderRadius: 4 }}
                  />
                  {upiId && (
                    <p className="mt-2" style={{ fontSize: 14, fontWeight: 700 }}>{upiId}</p>
                  )}
                  <p className="mt-1" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                    Scan this QR at delivery to pay
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Price details */}
        {card(
          <>
            <h3 className="fx-label mb-2">Price Details</h3>
            {summaryRow('Subtotal', `₹${originalAmount || totalAmount}`)}
            {couponCode && couponDiscount > 0 &&
              summaryRow(`Coupon (${couponCode})`, `-₹${couponDiscount}`, { color: 'var(--fx-green)' })}
            {walletAmountUsed > 0 &&
              summaryRow('Wallet Credit', `-₹${walletAmountUsed}`, { color: 'var(--fx-green)' })}

            <div className="flex items-center justify-between py-1.5">
              <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Shipping Fee</span>
              <span style={{ fontSize: 14 }}>
                {shippingCharge > 0
                  ? `₹${shippingCharge}`
                  : <span style={{ color: 'var(--fx-green)', fontWeight: 700 }}>FREE</span>}
              </span>
            </div>

            {summaryRow('GST', `₹${taxAmount || 0}`)}
            {Number(handlingCharge || 0) > 0 &&
              summaryRow('Platform Fee', `₹${Number(handlingCharge).toFixed(0)}`)}

            <div className="mt-2 pt-3" style={{ borderTop: '1px solid var(--fx-line)' }}>
              {summaryRow('Total Paid', `₹${Number(totalAmount || 0).toFixed(0)}`, { bold: true })}
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button onClick={onContinueShopping} className="fx-btn fx-btn-ghost flex-1">
            <Home size={17} />
            Continue Shopping
          </button>
          <button onClick={onViewOrders} className="fx-btn fx-btn-primary flex-1">
            <Package size={17} />
            Track Order
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="text-center pt-2">
          <button
            type="button"
            onClick={() => onContactSupport && onContactSupport()}
            style={{ fontSize: 13, color: 'var(--fx-pink)', fontWeight: 700 }}
          >
            Questions? Contact Support
          </button>
        </div>

      </div>
    </div>
  );
};

export default OrderConfirmationModal;
